import { toast } from 'react-hot-toast/headless'
import i18n from '../../i18n'
import api from '../../api'
import { Export } from '../../types'
import Button from '../../ui/Button'
import { CloseIcon, DownloadIcon } from '../../ui/icons'
import './ExportToast.css'

const POLL_INTERVAL = 5000

// Exports of millions of users can outlast the tab. Stop watching after this
// and let the completion email take over rather than polling forever.
const MAX_WATCH_MS = 30 * 60 * 1000

// Long enough for the exit animation, short enough that closing feels
// immediate. The toaster's own default is a full second of the card sitting
// there unchanged after the click.
const REMOVE_DELAY = 200

const tracked = new Set<number>()

interface ToastProps {
    title: string
    detail: string
    onDismiss: () => void
    pending?: boolean
    download?: () => void
}

const ExportToast = ({ title, detail, onDismiss, pending, download }: ToastProps) => (
    <div className="export-toast">
        {pending && <div className="export-toast-spinner" aria-hidden="true" />}
        <div className="export-toast-body">
            <div className="export-toast-title">{title}</div>
            <div className="export-toast-detail">{detail}</div>
        </div>
        <div className="export-toast-actions">
            {download && (
                <Button size="small" icon={<DownloadIcon />} onClick={download}>
                    {i18n.t('download')}
                </Button>
            )}
            <Button
                size="small"
                variant="secondary"
                icon={<CloseIcon />}
                aria-label={i18n.t('dismiss')}
                onClick={onDismiss}
            />
        </div>
    </div>
)

// Reusing the id upserts the same toast, so the card keeps its place in the
// stack as it moves from spinner to ready.
const show = (id: string, props: Omit<ToastProps, 'onDismiss'>) => {
    toast.custom(
        <ExportToast {...props} onDismiss={() => toast.dismiss(id)} />,
        {
            id,
            duration: Infinity,
            position: 'bottom-right',
            removeDelay: REMOVE_DELAY,
        },
    )
}

/**
 * Watches an export the user just started, spinning until the file is ready
 * and then offering the download. Deliberately in-memory only: a refresh drops
 * the watch and the completion email remains the durable notification.
 */
export const trackExport = (projectId: number | string, record: Export) => {
    if (tracked.has(record.id)) return
    tracked.add(record.id)

    const id = `export-${record.id}`
    const name = record.name ?? ''

    show(id, {
        pending: true,
        title: i18n.t('export_pending_title'),
        detail: i18n.t('export_pending_detail', { name }),
    })

    const startedAt = Date.now()
    const stop = () => {
        clearInterval(timer)
        tracked.delete(record.id)
    }

    const timer = setInterval(() => {
        if (Date.now() - startedAt > MAX_WATCH_MS) {
            stop()
            show(id, {
                title: i18n.t('export_slow_title'),
                detail: i18n.t('export_slow_detail', { name }),
            })
            return
        }

        api.exports.get(projectId, record.id)
            .then(latest => {
                if (latest.state === 'complete') {
                    stop()
                    show(id, {
                        title: i18n.t('export_ready_title'),
                        detail: i18n.t('export_ready_detail', {
                            name,
                            rows: latest.row_count.toLocaleString(),
                        }),
                        download: () => {
                            window.location.href = api.exports.downloadUrl(projectId, latest.id)
                            toast.dismiss(id)
                        },
                    })
                } else if (latest.state === 'failed') {
                    stop()
                    show(id, {
                        title: i18n.t('export_failed_title'),
                        detail: latest.error ?? i18n.t('export_failed_detail', { name }),
                    })
                }
            })
            .catch(() => { /* transient: the next tick retries */ })
    }, POLL_INTERVAL)
}
